"use client";

import React, { ReactNode } from "react";
import { usePathname } from "next/navigation";
import LGSTradesItemsView from "modules/lgs/components/stores/table/LGSTradesItemsView";
import TradeSectionTitle from "modules/lgs/components/stores/table/TradeSectionTitle";
import { LGSTradesMobileFilterBar } from "modules/lgs/components/stores/mobile/LGSTradesMobileFilterBar";
import { cn } from "utils/utils";
import { Package, Search } from "lucide-react";
import { Dock, DockItem } from "modules/common/components/ui/dock";
import { InfiniteData } from "@tanstack/react-query";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { EmptyStateCard } from "modules/common/components/EmptyStateCard";
import MatchedCardsIcon from "modules/common/components/Icons/MatchedCardsIcon";

interface SectionData {
  data: InfiniteData<any> | undefined;
  isPending: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
  totalResults: number | undefined;
  hasData: boolean;
  isMatchedTrades: boolean;
}

interface LGSMobileTabsProps {
  lgsId: number;
  slug: string;
  matchedSection: SectionData;
  tradesSection: SectionData;
  wantsSection: SectionData;
  activeView: { name: string };
  hasActiveFilters: boolean;
  isSignedIn: boolean;
  isAuthLoaded?: boolean;
  /** The anonymous <VisitorCtaBar variant="stacked">, above the dock items. */
  cta?: ReactNode;
}

export function LGSMobileTabs({
  lgsId,
  slug,
  matchedSection,
  tradesSection,
  wantsSection,
  activeView,
  hasActiveFilters,
  isSignedIn,
  isAuthLoaded = true,
  cta,
}: LGSMobileTabsProps) {
  const pathname = usePathname();
  const pathPrefix = pathname?.startsWith("/x/") ? "/x/stores" : "/stores";
  const activeTab = pathname?.includes("/wants")
    ? "wants"
    : pathname?.includes("/matched")
      ? "matched"
      : "trades";

  const getEmptyStateConfig = (
    type: "match" | "trades" | "wants",
    totalResults: number | undefined
  ) => {
    // Prioritize showing filter message when filters are active
    const showFilterMessage = hasActiveFilters;

    if (showFilterMessage) {
      return {
        icon:
          type === "match" ? (
            <MatchedCardsIcon className="h-8 w-8" />
          ) : type === "trades" ? (
            <Package className="h-8 w-8" />
          ) : (
            <Search className="h-8 w-8" />
          ),
        signedOutTitle: "No Results",
        signedOutMessage:
          "No cards match your current filters. Try adjusting your search or filters.",
        signedInTitle: "No Results",
        signedInMessage:
          "No cards match your current filters. Try adjusting your search or filters.",
        ctaText: undefined,
      };
    }

    // Default messages when no filters are active
    switch (type) {
      case "match":
        return {
          icon: <MatchedCardsIcon className="h-8 w-8" />,
          signedOutTitle: "Sign up",
          signedOutMessage:
            "Sign in to see matched trades and connect with players at this store.",
          signedInTitle: "No Matched Cards Yet",
          signedInMessage:
            "Add cards to your wants list and we'll notify you when players at this store have them for trade.",
          ctaText: "Add to Wants List",
        };
      case "trades":
        return {
          icon: <Package className="h-8 w-8" />,
          signedOutTitle: "Sign up",
          signedOutMessage:
            "Sign up to see cards available for trade at this store.",
          signedInTitle: "No Cards for Trade Yet",
          signedInMessage:
            "Be the first to list cards for trade at this store.",
          ctaText: "Create List",
        };
      case "wants":
        return {
          icon: <Search className="h-8 w-8" />,
          signedOutTitle: "Sign up",
          signedOutMessage:
            "Sign up to see cards players are looking for at this store.",
          signedInTitle: "No Wanted Cards Yet",
          signedInMessage:
            "Be the first to post cards you're looking for at this store.",
          ctaText: "Create Wants List",
        };
    }
  };

  const viewAllLinkMap: Record<"match" | "trades" | "wants", string> = {
    match: `${pathPrefix}/${slug}/matched`,
    trades: `${pathPrefix}/${slug}/haves`,
    wants: `${pathPrefix}/${slug}/wants`,
  };

  const renderSection = (
    sectionData: SectionData,
    title: string,
    type: "match" | "trades" | "wants"
  ) => {
    const currentCount =
      sectionData.data?.pages.flatMap((page) => page.items).length || 0;
    const emptyConfig = getEmptyStateConfig(type, sectionData.totalResults);

    // Don't show section title header for matched section when user is signed out
    // (there's no data to filter for unauthenticated users)
    const isSignedOutMatched = type === "match" && !isSignedIn;

    return (
      <div className={cn("flex flex-col")}>
        {!isSignedOutMatched && (
          <TradeSectionTitle
            currentCount={type === "match" ? undefined : currentCount}
            total={type === "match" ? undefined : sectionData.totalResults}
            title={title}
            type={type}
            viewAllLink={viewAllLinkMap[type]}
            leftContent={<LGSTradesMobileFilterBar storeId={lgsId} />}
            isLoading={sectionData.isPending || !isAuthLoaded}
          />
        )}
        <div className="h-full flex-1">
          {sectionData.hasData || !isAuthLoaded ? (
            <LGSTradesItemsView
              data={sectionData.data}
              isPending={sectionData.isPending || !isAuthLoaded}
              hasNextPage={sectionData.hasNextPage}
              isFetchingNextPage={sectionData.isFetchingNextPage}
              fetchNextPage={sectionData.fetchNextPage}
              isMatchedTrades={sectionData.isMatchedTrades}
              activeView={activeView}
            />
          ) : !isSignedIn ? (
            <EmptyStateCard
              variant="signed-out"
              icon={emptyConfig.icon}
              title={emptyConfig.signedOutTitle}
              description={emptyConfig.signedOutMessage}
              showWaitlistCTA={true}
              className="min-h-[35dvh]"
            />
          ) : (
            <EmptyStateCard
              variant="signed-in"
              icon={emptyConfig.icon}
              title={emptyConfig.signedInTitle}
              description={emptyConfig.signedInMessage}
              ctaText={emptyConfig.ctaText}
              ctaLink={emptyConfig.ctaText ? "/onboarding" : undefined}
              className="min-h-[35dvh]"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Active Section Content */}
      {activeTab === "matched" &&
        renderSection(matchedSection, "Matches", "match")}
      {activeTab === "trades" &&
        renderSection(tradesSection, "Haves", "trades")}
      {activeTab === "wants" && renderSection(wantsSection, "Wants", "wants")}

      {/* Bottom Navigation - Fixed at bottom */}
      <Dock header={cta}>
        <DockItem
          href={`${pathPrefix}/${slug}/haves`}
          icon={<Package size={ICON_SIZE} />}
          isActive={activeTab === "trades"}
        >
          Haves
        </DockItem>
        <DockItem
          href={`${pathPrefix}/${slug}/wants`}
          icon={<Search size={ICON_SIZE} />}
          isActive={activeTab === "wants"}
        >
          Wants
        </DockItem>
        <DockItem
          href={`${pathPrefix}/${slug}/matched`}
          icon={<MatchedCardsIcon fontSize={ICON_SIZE} />}
          isActive={activeTab === "matched"}
        >
          Matches
        </DockItem>
      </Dock>
    </>
  );
}
